import React, { useState, useRef, useMemo, useEffect } from 'react';
import { PropTypes } from 'prop-types';

const BoxInputField = ({ id, name, initialValue, callback, fieldLength, disabled, fieldSeperator, className, answerRequired, describedBy }) => {
  let defaultValue = (new Array(fieldLength)).fill({ value: '', changed: false });
  if (initialValue) {
    defaultValue = initialValue.split('').map(x => ({ value: x, changed: false }));
    if (defaultValue.length > fieldLength) {
      defaultValue = defaultValue.slice(0, fieldLength - 1);
    } else if (defaultValue.length < fieldLength) {
      defaultValue = defaultValue.concat((new Array(fieldLength - defaultValue.length)).fill({ value: '', changed: false }));
    }
  }
  const [pin, setPin] = useState(defaultValue);
  const fullEntry = useMemo(() => Object.values(pin).reduce((t, { value }) => t + value, ''), [pin]);
  const inputRef = [...Array(fieldLength).keys()].map(x => useRef(null));
  const regex = /^["'0-9'"]*$/;

  function onKeyHandler(event, index) {
    if (event === null) {
      return;
    }

    if (event.shiftKey || event.keyCode === 9 || event.keyCode === 16) {
      return;
    }

    if (event.keyCode === 8) {
      if (index !== 0) {
        const previous = inputRef[index - 1];
        if (previous) {
          previous.current.focus();
        }
      }
    } else {
      const next = inputRef[index + 1];
      if (next && event.target.value !== '') {
        next.current.focus();
      }
    }
  }

  function handleChange(event, fieldIndex) {
    if (event === null || event.target === null) {
      return;
    }

    if (!regex.test(event.target.value) || (event.target.value != null && event.target.value.length > 1)) {
      return;
    }

    setPin(pin.map((fieldInput, i) => (i === fieldIndex ? {
      changed: true,
      value: event.target.value
    } : fieldInput)));
  }

  useEffect(() => {
    callback(fullEntry);
  }, [pin]);

  return (
    <div id={ id }>
      {Array.from({ length: fieldLength }, (element, index) => (
        <span key={ `item-${ index }` }>
          <input
            id={ `tin-field-${ index + 1 }` }
            name={ name }
            aria-label={ name }
            disabled={ disabled }
            className={ className }
            ref={ inputRef[index] ? inputRef[index] : '' }
            maxLength={ 1 }
            type="text"
            onKeyUp={ e => onKeyHandler(e, index) }
            onChange={ e => handleChange(e, index) }
            value={ pin[index] && pin[index].value && !disabled ? pin[index].value : '' }
            aria-required={ answerRequired && !disabled ? 'true' : undefined }
            aria-describedby={ describedBy }
            required={ answerRequired && !disabled }
          />
          { fieldSeperator && (fieldSeperator.indexOf(index) >= 0) && <span className="box-input-seperator">-</span>}
        </span>
      ))}
    </div>
  );
};

BoxInputField.propTypes = {
  id: PropTypes.string.isRequired,
  name: PropTypes.string,
  initialValue: PropTypes.string,
  fieldLength: PropTypes.number,
  fieldSeperator: PropTypes.arrayOf(PropTypes.number),
  disabled: PropTypes.bool,
  callback: PropTypes.func,
  className: PropTypes.string,
  answerRequired: PropTypes.bool,
  describedBy: PropTypes.string
};

BoxInputField.defaultProps = {
  name: null,
  initialValue: null,
  fieldLength: 1,
  fieldSeperator: null,
  disabled: false,
  callback: null,
  className: '',
  answerRequired: false,
  describedBy: null
};

export default BoxInputField;
